import { useState } from "react";
import { Shield, CheckCircle, XCircle, AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface AccessControlPanelProps {
  plateNumber?: string;
  driverName?: string;
  onDecision: (decision: "PASS" | "HOLD" | "REJECT", reason?: string) => void;
}

export default function AccessControlPanel({ plateNumber, driverName, onDecision }: AccessControlPanelProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [complianceChecks, setComplianceChecks] = useState({
    vehicleRegistration: "checking",
    driverLicense: "checking", 
    insurance: "checking",
    hazmatCert: "checking",
    weightRestrictions: "checking"
  });

  const handleDecision = async (decision: "PASS" | "HOLD" | "REJECT", reason?: string) => {
    setIsProcessing(true);
    
    // Simulate compliance checking
    setTimeout(() => {
      setComplianceChecks({
        vehicleRegistration: Math.random() > 0.1 ? "pass" : "fail",
        driverLicense: Math.random() > 0.15 ? "pass" : "fail",
        insurance: Math.random() > 0.05 ? "pass" : "fail", 
        hazmatCert: Math.random() > 0.2 ? "pass" : "fail",
        weightRestrictions: Math.random() > 0.1 ? "pass" : "fail"
      });
      
      setIsProcessing(false);
      onDecision(decision, reason);
    }, 2000);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pass":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "fail":
        return <XCircle className="w-4 h-4 text-red-600" />;
      case "checking":
        return <Clock className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pass":
        return "bg-green-100 text-green-800";
      case "fail":
        return "bg-red-100 text-red-800";
      case "checking":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-orange-100 text-orange-800";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Shield className="w-5 h-5 mr-2" />
          Access Control & Compliance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Vehicle Info */}
          {plateNumber && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">License Plate</label>
                  <div className="text-lg font-bold">{plateNumber}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Driver</label>
                  <div className="text-lg font-bold">{driverName || "Unknown"}</div>
                </div>
              </div>
            </div>
          )}

          {/* Compliance Checks */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-gray-700">Compliance Verification</h4>
            
            <div className="space-y-2">
              {Object.entries(complianceChecks).map(([check, status]) => (
                <div key={check} className="flex items-center justify-between">
                  <span className="text-sm capitalize">{check.replace(/([A-Z])/g, ' $1')}</span>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(status)}
                    <Badge className={getStatusColor(status)}>
                      {status === "checking" ? "Verifying" : status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Decision Buttons */}
          <div className="grid grid-cols-3 gap-2 pt-4">
            <Button
              onClick={() => handleDecision("PASS")}
              disabled={isProcessing}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="button-access-pass"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              GRANT ACCESS
            </Button>
            
            <Button
              onClick={() => handleDecision("HOLD", "Manual inspection required")}
              disabled={isProcessing}
              variant="outline"
              className="border-orange-600 text-orange-600 hover:bg-orange-50"
              data-testid="button-access-hold"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              HOLD
            </Button>
            
            <Button
              onClick={() => handleDecision("REJECT", "Compliance failure")}
              disabled={isProcessing}
              variant="destructive"
              data-testid="button-access-reject"
            >
              <XCircle className="w-4 h-4 mr-2" />
              REJECT
            </Button>
          </div>

          {isProcessing && (
            <div className="text-center py-4">
              <div className="text-sm text-blue-600">Processing access decision...</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}